import { Flex, Checkbox, useMediaQuery } from '@chakra-ui/react';
import Guest from '@data/models/Guest';
import ScreenSize from '../../constants/screen-sizes';
import { StyledSection, StyledColumnWrapper } from './StyledGuestForm';
import { useTranslation } from 'react-i18next';
import { useStore } from '@data/other/withStore';

import countryData from '@data/data/countries.json';
import { observer } from 'mobx-react';
import { UseFormResult } from '@molecules/FormFactory/useForm';
import { FormValidation } from '@data/other/validation';
import { DropdownOptionItem } from '@molecules/FormFactory/Forms/Dropdown/DropdownOption/DropdownOptionProps';

interface GuestFormProps {
  isAddition?: boolean;
  defaultValues?: Guest;
  useFormResult: UseFormResult<Guest>;
}

const GENDER_OPTIONS: DropdownOptionItem[] = [
  {
    id: 'male',
    text: 'Male',
    value: 'M',
  },
  {
    id: 'female',
    text: 'Female',
    value: 'F',
  },
  {
    id: 'n/a',
    text: 'Prefer not to say',
    value: 'N/A',
  },
];
const COUNTRY_OPTIONS: DropdownOptionItem[] = countryData.map((country) => ({
  id: country.code,
  text: country.name,
  value: country.code,
}));

const validation: FormValidation<Guest> = {
  ...Guest.validationModel,
};

const GuestForm = ({
  isAddition = false,
  defaultValues,
  useFormResult,
}: GuestFormProps) => {
  const { property } = useStore();
  const { t } = useTranslation();

  const askGender = property.model?.settings.askGender || false;
  const askGuestTaxID = property.model?.settings.askGuestTaxID || false;
  const askNationality = property.model?.settings.askNationality || false;
  const askPostalAddress = property.model?.settings.askPostalAddress || false;
  const askCompanyName = property.model?.settings.askCompanyName || false;
  const askCompanyTaxID = property.model?.settings.askCompanyTaxID || false;

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const { Form, Input, Dropdown } = useFormResult;

  return (
    <Form defaultValues={defaultValues} validation={validation}>
      <Flex gap="6" direction="column" pt="6">
        <Flex
          gap={isMobile ? '6' : '4'}
          direction={isMobile ? 'column' : 'row'}
        >
          <Input.Text name="firstName" label={t('first-name')} />
          <Input.Text name="lastName" label={t('last-name')} />
        </Flex>
        <Flex
          gap={isMobile ? '6' : '4'}
          direction={isMobile ? 'column' : 'row'}
        >
          <Input.Text name="email" label={t('email')} />
          <Input.Text name="phone" label={t('phone')} />
        </Flex>

        {/* Gender */}
        {askGender && (
          <StyledColumnWrapper>
            <Dropdown
              name="gender"
              label={t('gender')}
              labelPosition="default"
              options={GENDER_OPTIONS}
              placeholder={t('select-gender')}
            />
          </StyledColumnWrapper>
        )}

        {/* Guest Tax ID */}
        {askGuestTaxID && (
          <Input.Text name="taxIdNumber" label={t('guest-tax-id')} />
        )}

        {/* Nationality */}
        {(askNationality || askPostalAddress) && (
          <Dropdown
            name="country"
            label={t('country')}
            labelPosition="default"
            options={COUNTRY_OPTIONS}
            placeholder={t('select-country')}
          />
        )}

        {/* Address */}
        {askPostalAddress && (
          <>
            <StyledSection size="lg" weight="600">
              {t('address-details')}
            </StyledSection>
            {isAddition && (
              <Checkbox name="same_address">
                Use same address as the primary guest
              </Checkbox>
            )}
            <Input.Text name="address1" label={t('street-address')} />
            <Input.Text name="address2" label={t('apt-suite-floor-etc')} />
            <Flex
              gap={isMobile ? '6' : '4'}
              direction={isMobile ? 'column' : 'row'}
            >
              <Dropdown
                name="state"
                label={t('state')}
                labelPosition="default"
                options={[]}
                placeholder={t('select-state')}
              />
              <Input.Text name="city" label={t('city')} />
            </Flex>
            <StyledColumnWrapper>
              <Input.Text name="zipCode" label={t('zip-postal-code')} />
            </StyledColumnWrapper>
          </>
        )}

        {(askCompanyName || askCompanyTaxID) && (
          <StyledSection size="lg" weight="600">
            {t('company-details')}
          </StyledSection>
        )}
        {askCompanyName && (
          <Input.Text name="companyName" label={t('company-name')} />
        )}
        {askCompanyTaxID && (
          <Input.Text name="companyTaxIdNumber" label={t('company-tax-id')} />
        )}

        {/*
        <Divider my="8px" />
        <BEInput required label="Custom Field Name (Input)" />
        <BETextarea required label="Custom Field Name (text area)" />
        */}
      </Flex>
    </Form>
  );
};

export default observer(GuestForm);
